import { useEffect, useRef } from "preact/hooks";
import { Share2, ThumbsDown, ThumbsUp } from "lucide-preact";

import type { EngagementType } from "./engagement";
import type { ManifestEntry } from "./types";

/** Shared icon sizing for action-row buttons. Kept as a constant rather
 *  than a magic number sprinkled per-icon. */
const ACTION_ICON = 18;

export interface ArticleCardProps {
  entry: ManifestEntry;
  onOpen: (entry: ManifestEntry) => void;
  /** Engagement recorder — fires impression/click/like/hide/share so the
   * EngagementBuffer can flush them to the worker. Independent of the
   * local UI mutators below (`onLike`, `onHide`, `onShare`), which run
   * the optimistic state regardless of whether the network call lands. */
  onEngage?: (type: EngagementType, entry: ManifestEntry) => void;
  /** Toggle this card's liked state in the Feed-level set. */
  onLike?: (entry: ManifestEntry) => void;
  /** Hide this card from the feed (Feed-level filter). */
  onHide?: (entry: ManifestEntry) => void;
  /** Trigger the Web Share API / clipboard fallback. */
  onShare?: (entry: ManifestEntry) => void;
  /** Whether the current article is in the liked set. */
  liked?: boolean;
}

function formatRelative(ms: number): string {
  const diffMs = Date.now() - ms;
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export function ArticleCard({
  entry,
  onOpen,
  onEngage,
  onLike,
  onHide,
  onShare,
  liked,
}: ArticleCardProps) {
  // Count one impression the first time the card renders. (A viewport-based
  // observer is a refinement; mount is a fine v1 proxy for the 200-item feed.)
  const seen = useRef(false);
  useEffect(() => {
    if (seen.current) return;
    seen.current = true;
    onEngage?.("impression", entry);
  }, [entry.id]);

  return (
    <article class="card">
      {entry.image_url ? (
        <img
          class="card-thumb"
          src={entry.image_url}
          alt=""
          loading="lazy"
          // Self-hide on 404 / CORS / etc. so a broken hero URL
          // doesn't leave an empty box at the top of the card.
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      ) : null}
      {/* On desktop (>=720px) the card lays out horizontally — the image
        * becomes a thumbnail on the left and this body sits to its right;
        * on mobile it stacks (image banner on top). See `.card` in
        * styles.css. */}
      <div class="card-body">
        <header>
          <h3>
            <button class="card-title" onClick={() => onOpen(entry)}>
              {entry.title}
            </button>
          </h3>
          <div class="meta">
            {entry.publisher ? <span>{entry.publisher}</span> : null}
            <span>·</span>
            <span>{formatRelative(entry.published_at)}</span>
          </div>
        </header>
        {entry.summary ? <p class="card-summary">{entry.summary}</p> : null}
        {entry.tags && entry.tags.length > 0 ? (
          <ul class="tags">
            {entry.tags.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        ) : null}
        {onEngage || onLike || onHide || onShare ? (
          <div class="card-actions">
            {/* Thumbs up = `like` engagement: +1.0 to article tags in the
              * local interest vector (see track.ts INTEREST_DELTA) and a
              * `like` event to analytics. Persists pressed state via the
              * Feed-level `liked` set. */}
            <button
              class={liked ? "action thumbs-up active" : "action thumbs-up"}
              aria-label={liked ? "Thumbs up (on)" : "Thumbs up"}
              aria-pressed={liked ? "true" : "false"}
              onClick={() => {
                // Only record `like` on the false→true transition; engagement
                // vocab has no `unlike` and a re-fire would double-count
                // into rank/personas/analytics.
                if (!liked) onEngage?.("like", entry);
                onLike?.(entry);
              }}
            >
              <ThumbsUp size={ACTION_ICON} aria-hidden="true" />
            </button>
            {/* Thumbs down = `hide` engagement: -1.0 to article tags in the
              * local interest vector AND filters the card from the feed via
              * the Feed-level `hidden` set. Mirrors the old "Hide" semantics
              * — same negative signal flow, clearer affordance. */}
            <button
              class="action thumbs-down"
              aria-label="Thumbs down"
              onClick={() => {
                onHide?.(entry);
                onEngage?.("hide", entry);
              }}
            >
              <ThumbsDown size={ACTION_ICON} aria-hidden="true" />
            </button>
            <button
              class="action share"
              aria-label="Share"
              onClick={() => {
                onShare?.(entry);
                onEngage?.("share", entry);
              }}
            >
              <Share2 size={ACTION_ICON} aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>
    </article>
  );
}
